import { useState, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { Beta } from '@mc/design-system'
import { ShareModal, SOURCES } from '@mc/share'
import { TRACK_CONTENT_TYPES } from '@mc/track-event'
import styles from 'components/GlobalCarousel/GlobalCarousel.module.scss'
import { ViewClassShareButtonGroup } from '../../../../components'
import { trackVideoModalClose } from '../../../../events'
import { TCourse } from '../../../dataFetching'
import { usePageData } from '../../PageDataProvider'
import { InstructorCarouselProps } from '../types'
import { InstructorTile } from './InstructorTile'

const VideoModal = dynamic(() => import('components/VideoModal'), {
  ssr: false,
})

export const SubcategoryInstructorCarousel = ({
  content,
  carouselName,
  trackProps,
  TitleComponent,
  onClickNext,
  onClickPrev,
}: InstructorCarouselProps) => {
  const [currentContent, setCurrentContent] = useState<TCourse | null>(null)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const { slug: categorySlug } = usePageData()

  const shareData = useMemo(() => {
    const { slug, instructorName, title, id } = currentContent || {}

    return {
      course: { slug, instructorName, id },
      title: title || '',
      source: SOURCES.CLASS_GUEST_NEXT,
    }
  }, [currentContent])

  const onOpenVideoModal = (selectedContent: TCourse) => {
    setCurrentContent(selectedContent)
    setIsVideoModalOpen(true)
  }

  const onCloseVideoModal = () => {
    setCurrentContent(null)
    setIsVideoModalOpen(false)
    trackVideoModalClose({
      uuid: currentContent?.uuid as unknown as string,
      categorySlug,
    })
  }

  const onOpenShareModal = () => {
    setIsShareModalOpen(true)
    setIsVideoModalOpen(false)
  }

  const onCloseShareModal = () => {
    setIsShareModalOpen(false)
    setCurrentContent(null)
  }

  return (
    <Beta.CarouselModern
      title={TitleComponent}
      srLabel={carouselName}
      className={`row ${styles.carousel} ${styles.slimSeparators}`}
      onClickNext={onClickNext}
      onClickPrev={onClickPrev}
      overflow
    >
      <>
        {content.map((c, index) => (
          <InstructorTile
            content={c}
            setCurrentContent={onOpenVideoModal}
            carouselName={carouselName}
            trackProps={{ ...trackProps, tileIndex: index }}
            key={c.id}
          />
        ))}
        {!!currentContent && (
          <VideoModal
            show={isVideoModalOpen}
            onCloseModal={onCloseVideoModal}
            videoId={currentContent.brightcoveVideoId}
            mediaUuid={currentContent.mediaUuid}
            trackProps={{ uuid: currentContent.uuid, categorySlug }}
            author={currentContent.instructorName}
            tagline={currentContent.skill}
            contentId={`${currentContent.id}_${
              currentContent.brightcoveVideoId ?? currentContent.mediaUuid
            }`}
            contentTitle={currentContent.title}
            contentType={TRACK_CONTENT_TYPES.COURSE_TRAILER}
            courseId={currentContent.id}
            instructorAvatar={currentContent.instructorImage}
            buttonGroup={
              <ViewClassShareButtonGroup
                content={currentContent}
                onShareClickHandler={onOpenShareModal}
                trackProps={{
                  categorySlug,
                }}
              />
            }
          />
        )}
        {isShareModalOpen && (
          <ShareModal
            show={isShareModalOpen}
            onClose={onCloseShareModal}
            shareData={shareData}
            useDeprecated
          />
        )}
      </>
    </Beta.CarouselModern>
  )
}
